import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  VStack,
  useEditable,
} from "@chakra-ui/react";
import { FaClock } from "react-icons/fa";
import { act, useCallback, useContext, useEffect, useState } from "react";
import { DialogProvider, createDialogContext } from "../Common/Dialog";
import { KYDWhitelabelEvent as KYDEvent } from "@common/interfaces/KYDEvent";
import { KYDWhitelabelTicketType as KYDTicketType } from "@common/interfaces/KYDTicket";
import TicketTypeWidget from "./TicketTypeWidget";
import { Wallet } from "@common/interfaces/KYDUser";

export const JoinWaitlistModalContext = createDialogContext();

const JoinWaitlistModal = ({
  onCreateSellOrder = () => {},
  onCreateBuyOrder = () => {},
  onCancel = () => {},
  ticketTypes = [],
  isLoading,
  action,
  wallet,
  kydEvent,
}: {
  onCreateSellOrder?: ({
    ticketTypeId,
    ticketIds,
  }: {
    ticketTypeId: string;
    ticketIds: string[];
  }) => void;
  onCreateBuyOrder?: ({
    ticketTypeId,
    quantity,
    autoCancelSettings,
  }: {
    ticketTypeId: string;
    quantity: number;
    autoCancelSettings?: {
      enabled: boolean;
      daysBeforeEvent: number;
    };
  }) => void;
  onCancel?: () => void;
  ticketTypes: KYDTicketType[];
  isLoading: boolean;
  action: "buy" | "sell" | null;
  wallet: Wallet;
  kydEvent: KYDEvent;
}) => {
  const { isOpen, close, title, text } = useContext(JoinWaitlistModalContext);

  const [currentTicketType, setCurrentTicketType] = useState<{
    ticket_type_id: string;
    quantity: number;
  } | null>(null);

  const [autoCancelEnabled, setAutoCancelEnabled] = useState(false);
  const [selectedCancelDaysBeforeEvent, setSelectedCancelDaysBeforeEvent] =
    useState<number>(1);

  const resetCount = () => {
    setCurrentTicketType(null);
    setAutoCancelEnabled(false);
    setSelectedCancelDaysBeforeEvent(1);
  };

  useEffect(() => {
    if (!ticketTypes || ticketTypes.length === 0) {
      close();
    }
  }, [ticketTypes]);

  const setTicketTypeCount = useCallback(
    (ticketTypeId: string, quantity: number) => {
      if (quantity === 0) {
        setCurrentTicketType(null);
      } else {
        setCurrentTicketType(() => ({
          ticket_type_id: ticketTypeId,
          quantity,
        }));
      }
    },
    [currentTicketType, setCurrentTicketType]
  );

  const onCloseInternal = () => {
    // setCount(0);
    resetCount();
    close();
    onCancel();
  };

  const onConfirmInternal = async () => {
    if (action === "buy") {
      await onCreateBuyOrder({
        ticketTypeId: currentTicketType.ticket_type_id,
        quantity: currentTicketType.quantity,
        autoCancelSettings: {
          enabled: autoCancelEnabled,
          daysBeforeEvent: selectedCancelDaysBeforeEvent,
        },
      });
    } else if (action === "sell") {
      const ticket_type = wallet?.valid_waitlist_ticket_types.find(
        (tt) => tt.id === currentTicketType.ticket_type_id
      );
      const tickets = ticket_type.ticket_ids.slice(
        0,
        currentTicketType.quantity
      );
      await onCreateSellOrder({
        ticketTypeId: currentTicketType.ticket_type_id,
        ticketIds: tickets,
      });
    }

    close();
    resetCount();
  };

  const buttonText = {
    buy: "JOIN WAITLIST",
    sell: `LIST ${
      currentTicketType ? currentTicketType.quantity : 0
    } TICKET(S)`,
  };

  const hasCountChanged = currentTicketType && currentTicketType.quantity > 0;

  useEffect(() => {
    if (action === null && isOpen) {
      console.warn("JoinWaitlistModal with no action");
    }
  }, [action, isOpen]);

  const getWaitlistExpirationOptions = () => {
    const options = [];
    const eventStartAt = new Date(kydEvent.start_at);
    const now = new Date();
    const diff = eventStartAt.getTime() - now.getTime();
    const days = diff / (1000 * 60 * 60 * 24);
    if (days > 1) {
      options.push({ label: "24 hours before event", value: 1 });
    }
    if (days > 3) {
      options.push({ label: "3 days before event", value: 3 });
    }
    if (days > 7) {
      options.push({ label: "1 week before event", value: 7 });
    }
    return options;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? () => {} : onCloseInternal}
      isCentered
      closeOnOverlayClick={true}
    >
      <ModalOverlay />
      <ModalContent
        pos={["absolute", "relative"]}
        bottom={[0, "auto"]}
        mb={[0, "auto"]}
        borderBottomLeftRadius={[0, "md"]}
        borderBottomRightRadius={[0, "md"]}
      >
        <ModalCloseButton />
        <ModalHeader paddingTop={4}>
          <VStack gap={0} width={"full"}>
            <Text w="full">{title}</Text>
          </VStack>
        </ModalHeader>
        <ModalBody textAlign={"left"}>
          <VStack spacing={2}>
            <Text w="full" color="gray.600" fontSize={14}>
              {text}
            </Text>
            {action === "buy" ? (
              <VStack w="full">
                {ticketTypes.map((ticketType) => (
                  <TicketTypeWidget
                    key={ticketType.id}
                    ticketType={ticketType}
                    onChangeCount={(quantity) =>
                      setTicketTypeCount(ticketType.id, quantity)
                    }
                    disabled={
                      isLoading ||
                      (currentTicketType &&
                        ticketType &&
                        currentTicketType.ticket_type_id !== ticketType.id)
                    }
                    maximum={ticketType.limit}
                    hidePrice={false}
                    isListed={
                      !!wallet?.buy_waitlist_orders.find((wo) =>
                        ticketType.pool
                          ? wo.pool_id === ticketType.pool?.id
                          : wo.pool_id === ticketType.id
                      )
                    }
                  />
                ))}
                {getWaitlistExpirationOptions().length > 0 && (
                  <VStack
                    w="full"
                    spacing={4}
                    mt={6}
                    align="flex-start"
                    borderWidth={1}
                    borderColor="gray.300"
                    borderRadius="8px"
                    p={4}
                  >
                    <HStack w="full" justify="space-between" align="center">
                      <HStack spacing={3} align="center">
                        <Icon as={FaClock} color="white.500" boxSize="16px" />
                        <Text fontSize="14px" color="gray.700" fontWeight="400">
                          Automatically cancel my request
                        </Text>
                      </HStack>
                      <Checkbox
                        isChecked={autoCancelEnabled}
                        onChange={(e) => setAutoCancelEnabled(e.target.checked)}
                        colorScheme="blue"
                        size="md"
                      />
                    </HStack>
                    {autoCancelEnabled && (
                      <Box w="full">
                        <Select
                          value={selectedCancelDaysBeforeEvent}
                          onChange={(e) =>
                            setSelectedCancelDaysBeforeEvent(
                              parseInt(e.target.value)
                            )
                          }
                          size="md"
                          borderRadius="8px"
                          borderColor="gray.300"
                          bg="white"
                          fontSize="14px"
                          color="gray.700"
                          sx={{
                            option: {
                              bg: "white",
                              color: "gray.700",
                            },
                            _focus: {
                              borderColor: "blue.400",
                              boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
                            },
                            _hover: {
                              borderColor: "gray.400",
                            },
                          }}
                        >
                          {getWaitlistExpirationOptions().map((option) => (
                            <option value={option.value}>{option.label}</option>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </VStack>
                )}
              </VStack>
            ) : null}
            {action === "sell"
              ? wallet?.valid_waitlist_ticket_types.map((ticketType) => (
                  <TicketTypeWidget
                    key={ticketType.id}
                    ticketType={{
                      id: ticketType.id,
                      name: ticketType.name,
                      description: ticketType.description,
                    }}
                    onChangeCount={(quantity) =>
                      setTicketTypeCount(ticketType.id, quantity)
                    }
                    disabled={
                      isLoading ||
                      (currentTicketType &&
                        ticketType &&
                        currentTicketType.ticket_type_id !== ticketType.id)
                    }
                    maximum={ticketType.quantity}
                    hidePrice={true}
                    isListed={
                      !!wallet?.sell_waitlist_orders.find(
                        (wo) => wo.pool_id === ticketType.pool_id
                      )
                    }
                  />
                ))
              : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={onConfirmInternal}
            isDisabled={!hasCountChanged || isLoading}
            disabled={!hasCountChanged || isLoading}
            isLoading={isLoading}
            sx={{
              width: "full",
              backgroundColor: "lightGreen",
              borderRadius: "xl",
              borderWidth: 2,
              borderColor: "darkGreen",
              color: "black",
              p: 6,
              fontSize: 18,
              fontWeight: "bold",
              _hover: {
                backgroundColor: "darkGreen",
              },
            }}
          >
            {buttonText[action]}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withWaitlistModal =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={JoinWaitlistModalContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default JoinWaitlistModal;
